import type { ReactNode } from "react";
import { Card } from "../primitives/Card";
import { ChartSkeleton } from "../primitives/Skeleton";
import { cn } from "../lib/cn";

export interface LegendItem {
  label: ReactNode;
  /** Swatch color (canvas hex from the palette). */
  color: string;
  /** Render the swatch as a dashed line instead of a filled chip. */
  line?: boolean;
}

export interface ChartFrameProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Header-right slot (period switch, menu). */
  actions?: ReactNode;
  /** Identity legend — always shown for ≥ 2 series (never color-alone). */
  legend?: LegendItem[];
  /** Plot area height in px. */
  height?: number;
  loading?: boolean;
  empty?: boolean;
  emptyLabel?: ReactNode;
  className?: string | undefined;
  children?: ReactNode;
}

/**
 * Shared chrome for every chart: a {@link Card} with header + identity legend
 * and built-in loading/empty states, so charts stay consistent and never render
 * a bare axis with no data.
 */
export function ChartFrame({
  title,
  subtitle,
  actions,
  legend,
  height = 260,
  loading = false,
  empty = false,
  emptyLabel = "No data for this range",
  className,
  children,
}: ChartFrameProps): ReactNode {
  return (
    <Card title={title} subtitle={subtitle} actions={actions} className={className}>
      {legend !== undefined && legend.length > 0 && (
        <ul className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {legend.map((item, i) => (
            <li key={i} className="inline-flex items-center gap-1.5 text-xs text-ink-secondary">
              {item.line ? (
                <span
                  className="inline-block h-0.5 w-3.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
              ) : (
                <span
                  className="inline-block size-2.5 rounded-[3px]"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
              )}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      )}

      {loading ? (
        <ChartSkeleton height={height} />
      ) : empty ? (
        <div
          className={cn(
            "flex items-center justify-center rounded-lg border border-dashed border-hairline text-sm text-muted",
          )}
          style={{ height }}
        >
          {emptyLabel}
        </div>
      ) : (
        children
      )}
    </Card>
  );
}
